/**
 * Print Docs Module
 *
 * We use this module to print security docs (for now. Later we can use it to create all sorts of pdf).
 * Each documents data will need to be generated in a different service file.
 * To learn more about how this package works, visit  https://pdfmake.github.io/docs/0.1/
 */

import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { generateOfferLetter } from '@/modules/shared/services/print-docs/offer-letter.service';
import { generateDSRSummary } from '@/modules/shared/services/print-docs/dsr-summary.service';
import { generateLoanProcessingChecklist } from '@/modules/shared/services/print-docs/loan-processing-checklist.service';
import { generatePreApprovalChecklist } from '@/modules/shared/services/print-docs/pre-approval-checklist.service';
import { generateAllComments } from '@/modules/shared/services/print-docs/all-comments.service';
import { generateCollateralSummary } from '@/modules/shared/services/print-docs/collateral-summary.service';
import { generateBillOfSales } from '@/modules/shared/services/print-docs/bill-of-sales.service';
import { generateLetterToCollectorate } from '@/modules/shared/services/print-docs/letter-to-collectorate.service';
import { printDocumentFonts } from '@/lib/constants/printDocumentFonts';
import { LoanDocumentNames } from '@/lib/constants/loanDocumentNames';
import { generateLoanApplication } from '@/modules/shared/services/print-docs/loan-application.service';
import { generateNetWorthSummary } from '@/modules/shared/services/print-docs/net-worth-summary.service.js';
import { generateLoanReviewChecklist } from '@/modules/shared/services/print-docs/loan-review-checklist.service.js';
import { generatePreDisbursementChecklist } from '@/modules/shared/services/print-docs/pre-disbursement.service.js';
import { generateInsuranceSummary } from '@/modules/shared/services/print-docs/insuranceSummary.service.js';
import { generateGuaranteeDoc } from '@/modules/shared/services/print-docs/guarantee.service.js';
import { generatePowerOfAttorney } from '@/modules/shared/services/print-docs/power-of-attorney.service.js';
import { generatePromissoryNote } from '@/modules/shared/services/print-docs/promissory-note.service.js';
import { generateLoanApprovalSummary } from '@/modules/shared/services/print-docs/loan-approval-summary.service.js';

const bundledVfs = pdfFonts.default || pdfFonts.pdfMake?.vfs || pdfFonts;
pdfMake.vfs = bundledVfs;

const bundledRobotoFonts = {
  normal: 'Roboto-Regular.ttf',
  bold: 'Roboto-Medium.ttf',
  italics: 'Roboto-Italic.ttf',
  bolditalics: 'Roboto-MediumItalic.ttf'
};

// Keep existing document font names, but back them with local pdfMake fonts.
pdfMake.fonts = Object.keys(printDocumentFonts).reduce(
  (fonts, fontName) => ({
    ...fonts,
    [fontName]: bundledRobotoFonts
  }),
  { Roboto: bundledRobotoFonts }
);

const sanitizePdfNode = (node) => {
  if (node instanceof Date) {
    return node;
  }

  if (Array.isArray(node)) {
    return node
      .filter((item) => item !== false && item !== null && item !== undefined)
      .map(sanitizePdfNode);
  }

  if (node && typeof node === 'object') {
    return Object.keys(node).reduce((sanitizedNode, key) => {
      const value = sanitizePdfNode(node[key]);
      if (value === false || value === null || value === undefined) {
        return sanitizedNode;
      }
      return {
        ...sanitizedNode,
        [key]: value
      };
    }, {});
  }

  return node;
};

const openPdf = (documentDefinition) => {
  const pdfWindow = window.open('', '_blank');

  if (pdfWindow) {
    pdfWindow.document.write(
      '<!doctype html><title>Generating document...</title><body style="font-family: Arial, sans-serif; padding: 24px;">Generating document...</body>'
    );
  }

  pdfMake.createPdf(sanitizePdfNode(documentDefinition)).getBlob((blob) => {
    const pdfUrl = URL.createObjectURL(blob);

    if (pdfWindow && !pdfWindow.closed) {
      pdfWindow.location.href = pdfUrl;
    } else {
      window.open(pdfUrl, '_blank');
    }

    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
  });
};

export const actions = {
  /**
   * This function is responsible for printing the document
   *
   * Just pass in an object with the document name and any other params needed to generate the document
   *
   * @param {Object} _ The vuex context(ignored)
   * @param {Object} payload
   * @param {string} payload.documentName - The name of the document to print
   * @param {Object} payload.params - The parameters needed to generate the document
   */
  printDocument: async (_, payload) => {
    try {
      const { documentName, params } = payload;
      let documentDefinition;

      switch (documentName) {
        case LoanDocumentNames.offerLetter:
          documentDefinition = generateOfferLetter(params || {});
          break;
        case LoanDocumentNames.debServiceRatioSummary:
          documentDefinition = generateDSRSummary(params || {});
          break;
        case LoanDocumentNames.loanProcessingChecklist:
          documentDefinition = generateLoanProcessingChecklist(params || {});
          break;
        case LoanDocumentNames.preApprovalChecklist:
          documentDefinition = generatePreApprovalChecklist(params || {});
          break;
        case LoanDocumentNames.allComments:
          documentDefinition = generateAllComments(params || {});
          break;
        case LoanDocumentNames.collateralSummary:
          documentDefinition = generateCollateralSummary(params || {});
          break;
        case LoanDocumentNames.billOfSales:
          documentDefinition = generateBillOfSales(params || {});
          break;
        case LoanDocumentNames.letterToCollectorate:
          documentDefinition = generateLetterToCollectorate(params || {});
          break;
        case LoanDocumentNames.loanApplication:
          documentDefinition = generateLoanApplication(params || {});
          break;
        case LoanDocumentNames.loanApprovalSummary:
          documentDefinition = generateLoanApprovalSummary(params || {});
          break;
        case LoanDocumentNames.netWorthSummary:
          documentDefinition = generateNetWorthSummary(params || {});
          break;
        case LoanDocumentNames.loanReviewChecklist:
          documentDefinition = generateLoanReviewChecklist(params || {});
          break;
        case LoanDocumentNames.preDisbursementChecklist:
          documentDefinition = generatePreDisbursementChecklist(params || {});
          break;
        case LoanDocumentNames.insuranceSummary:
          documentDefinition = generateInsuranceSummary(params || {});
          break;
        case LoanDocumentNames.guarantee:
          documentDefinition = generateGuaranteeDoc(params || {});
          break;
        case LoanDocumentNames.powerOfAttorney:
          documentDefinition = generatePowerOfAttorney(params || {});
          break;
        case LoanDocumentNames.promissoryNote:
          documentDefinition = generatePromissoryNote(params || {});
          break;

        default:
          break;
      }

      if (documentDefinition) {
        openPdf(documentDefinition);
      }
    } catch (error) {
      console.error('Error printing document', error);
    }
  }
};
